import { useCallback, useEffect, useState } from 'react'
import Layout from '@/components/layout/layout'
import { Box, Flex, Grid, Heading, Separator } from '@chakra-ui/react'
import ButtonMore from '@/components/layout/action/button-more'
import ModalActionsDashboard from '@/components/modal/modal-actions-dashboard'
import JwtPayload from '@/entities/api/jwt'
import { AppDispatch, RootState } from '@/store/store'
import { useDispatch, useSelector } from 'react-redux'
import Toast from '@/components/elements/toast'
import { sessionRefrestUpdate } from '@/store/features/state'
import SpinnerLoading from '@/components/elements/spinner'
import Search from '@/components/elements/search'
import NotData from '@/components/elements/not-data'
import CustomersEntity from '@/entities/api/customers/customers'
import { fetchCustomers } from '@/services/api/customers/customers'
import { setCustomersFn, setSelectCustomerFn } from '@/store/features/customers'
import CustomerCard from '@/components/cards/customer-card'
import Pagination from '@/components/elements/pagination'
import { usePagination } from '@/hooks/usePagination'

const Customers = ({ data }: { data: JwtPayload }) => {
	const [modalAddOpen, setmodalAddOpen] = useState<Boolean>(false)
	const [modalEditOpen, setmodalEditOpen] = useState<Boolean>(false)
	const [isLoading, setIsloading] = useState<boolean>(false)
	const [search, setSearch] = useState<string>()
	const [usersFilter, setUsersFilter] = useState<CustomersEntity[] | []>()
	const [isLastPage, setIsLastPage] = useState<boolean>(false)

	const customersRefrest = useSelector(
		(state: RootState) => state.state.customersRefrest
	)
	const token = useSelector((state: RootState) => state.auth.token)
	const customers = useSelector((state: RootState) => state.customers.customers)
	const dispatch = useDispatch<AppDispatch>()

	const {
		limit,
		offset,
		nextPage,
		prevPage,
		goToPage,
		setItemsPerPage,
		counterPages,
		isLoading: isPaginationLoading,
		setIsLoading: setPaginationLoading,
		currentPage,
		isFirstPage,
		handleEmptyResponse,
	} = usePagination({
		initialLimit: 10,
	})

	const fetchCustomersData = useCallback(async () => {
		try {
			setIsloading(true)
			setPaginationLoading(true)

			const response = await fetchCustomers(token ?? '', limit, offset)

			if (response?.status === 401) {
				dispatch(sessionRefrestUpdate())
				return
			}

			if (
				!response?.data ||
				response.data.length === 0 ||
				response.data.length < limit
			) {
				setIsLastPage(true)

				if (response?.data?.length === 0 && offset > 0) {
					handleEmptyResponse()
					return
				}
			} else {
				setIsLastPage(false)
			}

			dispatch(setCustomersFn(response?.data ?? []))
		} catch (error) {
			Toast({ type: 'error', message: 'No se pudo obtener los clientes' })
			if (offset > 0) {
				handleEmptyResponse()
			}
		} finally {
			setIsloading(false)
			setPaginationLoading(false)
		}
	}, [token, limit, offset, setPaginationLoading])

	useEffect(() => {
		fetchCustomersData()
	}, [fetchCustomersData, customersRefrest])

	useEffect(() => {
		setUsersFilter(customers)
	}, [customers])

	useEffect(() => {
		if (search && search.trim().length > 0) {
			const term = search.toLowerCase()
			setUsersFilter(
				customers?.filter(
					(cat) =>
						cat.name.toLowerCase().includes(term) ||
						cat.role.toLowerCase().includes(term) ||
						cat.email.toLowerCase().includes(term) ||
						cat.phone.toString().includes(term) ||
						cat.lastname.toLowerCase().includes(term)
				) ?? []
			)
			return
		}

		setUsersFilter(customers ?? [])
	}, [search, customers])

	const openEditModal = async (customer: CustomersEntity) => {
		dispatch(setSelectCustomerFn(customer))
		setmodalEditOpen(true)
	}

	return (
		<Layout data={data}>
			{isLoading && !isPaginationLoading ? (
				<SpinnerLoading />
			) : (
				<>
					<Flex w='full' p='4' direction='column' gap='6'>
						<Heading
							as='h2'
							color='black'
							fontSize={{ base: '2xl', sm: '3xl' }}
							textAlign={{ base: 'center', lg: 'start' }}
							fontWeight='800'
							whiteSpace='normal'
							wordBreak='break-word'
						>
							Gestion de clientes
						</Heading>

						<Flex
							w='full'
							direction={{ base: 'column', lg: 'row' }}
							align='center'
							justify='space-between'
							gap='4'
							flexWrap='wrap'
						>
							<ButtonMore click={() => setmodalAddOpen(true)} />

							<Flex
								w={{ base: 'full', lg: 'auto' }}
								wrap='wrap'
								gap='4'
								justify={{ base: 'start', md: 'center' }}
							>
								<Box w={{ base: 'full', sm: 'auto' }}>
									<Search setSearch={setSearch} />
								</Box>
							</Flex>
						</Flex>
					</Flex>

					<Separator w='full' />

					<Box w='full' p='6' position='relative'>
						{isPaginationLoading && <SpinnerLoading size='lg' />}

						{!isPaginationLoading && usersFilter && usersFilter.length > 0 ? (
							<Grid
								w='full'
								templateColumns={{
									base: '1fr',
									sm: 'repeat(2, 1fr)',
									lg: 'repeat(4, 1fr)',
									xl: 'repeat(6, 1fr)',
								}}
								gap='4'
								position='relative'
							>
								{usersFilter.map((customer, index) => {
									if (customer._id !== data.sub) {
										return (
											<CustomerCard
												key={customer._id ?? index}
												token={token ?? ''}
												customer={customer}
												click={() => openEditModal(customer)}
											/>
										)
									} else if (customers.length === 1) {
										return (
											<Box key='no-data' w='full' position='absolute'>
												<NotData />
											</Box>
										)
									} else {
										return null
									}
								})}
							</Grid>
						) : (
							<NotData />
						)}

						<Pagination
							totalPages={Math.ceil(customers.length / limit)}
							limit={limit}
							offset={offset}
							totalItems={customers.length}
							counterPages={counterPages}
							onPrevPage={prevPage}
							onNextPage={nextPage}
							onGoToPage={goToPage}
							onSetItemsPerPage={setItemsPerPage}
							isLoading={isPaginationLoading}
							currentPage={currentPage}
							isFirstPage={isFirstPage}
							isLastPage={isLastPage}
						/>
					</Box>

					{modalAddOpen && (
						<ModalActionsDashboard
							open={modalAddOpen}
							action='add-customer'
							close={() => setmodalAddOpen(false)}
						/>
					)}
					{modalEditOpen && (
						<ModalActionsDashboard
							open={modalEditOpen}
							action='edit-customer'
							close={() => setmodalEditOpen(false)}
						/>
					)}
				</>
			)}
		</Layout>
	)
}

export default Customers
